/**
 * MAHI — top-level router. Mounted at / (the domain root) from the main App.
 *
 * Only the three screens someone can actually land on cold — Login, Signup,
 * Discover — are in the main bundle. Everything else is a lazy chunk.
 *
 * These 22 pages used to be static imports, which meant the partner admin
 * console, Style Studio, chat history and the four profile sub-pages were all
 * downloaded and parsed before the first dress could be painted. On a phone
 * the parse alone is not free, and none of it is on the path to a product.
 *
 * Onboarding and Wardrobe carry `webpackPrefetch` rather than waiting to be
 * navigated to: both are near-certain next destinations (Onboarding directly
 * after signup, Wardrobe one tap from the feed), and a prefetch link fetches
 * them at browser-idle priority, so they are warm in the HTTP cache without
 * ever competing with the feed or its images.
 */
import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { MahiAuthProvider, useMahiAuth } from './auth';
import MahiLogin from './pages/Login';
import MahiSignup from './pages/Signup';
import MahiDiscover from './pages/Discover';
import MahiLogo from './components/ui/MahiLogo';
import MahiRouteLoader from './components/ui/MahiRouteLoader';
import { shouldPlayMahiIntro } from './components/ui/mahiIntroGate';

const MahiOnboarding = lazy(() => import(/* webpackPrefetch: true */ './pages/Onboarding'));
const MahiWardrobe = lazy(() => import(/* webpackPrefetch: true */ './pages/Wardrobe'));
const MahiHome = lazy(() => import('./pages/Home'));
const AccountDetails = lazy(() => import('./pages/AccountDetails'));
const Feedback = lazy(() => import('./pages/Feedback'));
const Invite = lazy(() => import('./pages/Invite'));
const AlmostYours = lazy(() => import('./pages/AlmostYours'));
const ChatHistory = lazy(() => import('./pages/ChatHistory'));
const ChatHistoryDetail = lazy(() => import('./pages/ChatHistoryDetail'));
const StyleDna = lazy(() => import('./pages/StyleDna'));
const Memory = lazy(() => import('./pages/Memory'));
const Preferences = lazy(() => import('./pages/Preferences'));
const FeedPreferences = lazy(() => import('./pages/FeedPreferences'));
const Privacy = lazy(() => import('./pages/Privacy'));
const PartnerSignup = lazy(() => import('./pages/PartnerSignup'));
const PartnerAdmin = lazy(() => import('./pages/PartnerAdmin'));
const PartnerVerify = lazy(() => import('./pages/PartnerVerify'));
const Terms = lazy(() => import('./pages/Terms'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

// Shared by the auth gate and the lazy-chunk boundary — a route that is still
// deciding whether you may see it and a route whose code is still arriving
// are the same thing from the user's side, and showing two different waiting
// states for them would just look like a flicker.
const RouteFallback = () => <MahiRouteLoader />;

// The one route that waits behind the wordmark instead of behind the robot.
//
// A cold load into /discover paints three things in a row: the pre-JS boot
// mark inlined in public/index.html, then whatever the auth gate shows while
// /auth/me is in flight, then the session-entry animation. With the robot in
// the middle that sequence is logo, robot, logo — it reads as three separate
// loading screens rather than one arrival. This is deliberately the *same*
// mark at the *same* 8rem width and the same slate as the boot skeleton and
// as MahiIntro's first frame, so nothing swaps out: the logo is simply on
// screen from first paint until the letters start moving.
//
// Only while the intro is actually pending. Once it has played this session,
// /discover is an ordinary route and gets the ordinary robot back.
const IntroFallback = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white">
    <MahiLogo className="w-32 h-auto text-slate-700 animate-mahi-boot-pulse" decorative />
  </div>
);

const Guarded = ({ children, requireOnboarded = false, requireRegistered = false,
                  anonymousFallback = '/login' }) => {
  const { user, loading } = useMahiAuth();
  const location = useLocation();
  if (loading) {
    return location.pathname.startsWith('/discover') && shouldPlayMahiIntro()
      ? <IntroFallback />
      : <RouteFallback />;
  }
  // `!user` is now genuinely exceptional rather than the ordinary first-visit
  // state: auth.js mints a guest session for anyone without one, so getting
  // here with no user means minting itself failed (offline, rate-limited,
  // backend down). `anonymousFallback` is where that lands.
  if (!user) return <Navigate to={anonymousFallback} replace />;
  // Screens that need a person rather than a session — a photo, a phone
  // number, somewhere to send an invite. The backend enforces this too
  // (require_registered_user); this only saves a guest from loading a screen
  // whose every request would come back 409.
  if (requireRegistered && user.is_guest) {
    return <Navigate to="/signup" replace state={{ from: location.pathname }} />;
  }
  // A user who dropped off mid-onboarding and returns later (bookmark, tab
  // reopen) must finish onboarding before reaching a screen that assumes a
  // name and photo. Guests are exempt by construction: they have no
  // onboarding to be part-way through, and this flag is precisely what used
  // to stand between a first-time visitor and their first card.
  if (requireOnboarded && !user.is_guest && (user.onboarding_step ?? 1) < 5) {
    return <Navigate to={`/onboarding/${user.onboarding_step || 1}`} replace />;
  }
  return children;
};

const MahiApp = () => (
  <MahiAuthProvider>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="login" element={<MahiLogin />} />
        {/* Welcome (pre-signup pitch carousel) retired as the entry flow —
            onboarding itself now carries MAHI's introduction, one line per
            step, so a separate multi-slide pitch before signup was pure
            friction. Welcome.jsx is kept, unrouted, in case it's revived. */}
        <Route path="signup" element={<MahiSignup />} />
        <Route path="onboarding/:step" element={<Guarded><MahiOnboarding /></Guarded>} />
        <Route path="onboarding" element={<Navigate to="/onboarding/1" replace />} />
        <Route path="home" element={<Guarded requireOnboarded><MahiHome /></Guarded>} />
        {/* New swipeable discovery feed — under review in phases, not yet
            the default post-onboarding destination. See Discover.jsx. */}
        <Route path="discover" element={<Guarded requireOnboarded><MahiDiscover /></Guarded>} />
        {/* The bare domain root renders Discover in place rather than
            redirecting to /discover — the same screen, mounted a second
            time under a second path, so a signed-in visitor to
            mahi.fashion/ simply stays on mahi.fashion/ instead of watching
            the address bar grow a path the moment the page loads.
            /discover remains the canonical route every in-app navigate()
            call still targets; this is purely a front-door alias.
            `anonymousFallback="/signup"` keeps the one behavior that has to
            differ from an ordinary guarded route: someone arriving here
            signed out is a prospective signup, not a session gone stale. */}
        <Route index element={<Guarded requireOnboarded anonymousFallback="/signup"><MahiDiscover /></Guarded>} />
        {/* Wardrobe — everything swiped right. A second entry point from the
            profile section lands here too once profile exists; nothing about
            this route needs to change for that. */}
        <Route path="wardrobe" element={<Guarded requireOnboarded><MahiWardrobe /></Guarded>} />
        {/* Profile section, Phase 1 — see ProfilePanel.jsx for the panel itself,
            opened from the avatar on /discover. These are the "deeper
            sections" it navigates to. */}
        <Route path="profile/account" element={<Guarded requireOnboarded requireRegistered><AccountDetails /></Guarded>} />
        <Route path="feedback" element={<Guarded requireOnboarded><Feedback /></Guarded>} />
        <Route path="invite" element={<Guarded requireOnboarded requireRegistered><Invite /></Guarded>} />
        {/* Style Studio was the try-ons half of the closet. It is the Wardrobe
            now — same pieces, with the try-on shown as the tile wherever one
            exists. The route stays as a redirect: it is in people's history,
            and a bookmark that 404s is a worse answer than the screen that
            replaced it. */}
        <Route path="style-studio" element={<Navigate to="/wardrobe" replace />} />
        <Route path="almost-yours" element={<Guarded requireOnboarded><AlmostYours /></Guarded>} />
        <Route path="chat-history" element={<Guarded requireOnboarded><ChatHistory /></Guarded>} />
        <Route path="chat-history/:conversationId" element={<Guarded requireOnboarded><ChatHistoryDetail /></Guarded>} />
        {/* Profile section, Phase 3 */}
        <Route path="style-dna" element={<Guarded requireOnboarded><StyleDna /></Guarded>} />
        <Route path="profile/memory" element={<Guarded requireOnboarded><Memory /></Guarded>} />
        {/* "What you see" — the profile's door onto the discovery filter the
            first-run picker collects. Its own route rather than a section of
            profile/preferences: it is a hard filter on the feed, and a setting
            with that much reach should be as easy to find as it was to set. */}
        <Route path="profile/feed" element={<Guarded requireOnboarded><FeedPreferences /></Guarded>} />
        <Route path="profile/preferences" element={<Guarded requireOnboarded><Preferences /></Guarded>} />
        <Route path="profile/privacy" element={<Guarded requireOnboarded><Privacy /></Guarded>} />
        {/* Legal — public, no auth required, linked from Login/Signup */}
        <Route path="terms" element={<Terms />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        {/* Partner / brand portal — public, no auth required */}
        <Route path="partner/signup" element={<PartnerSignup />} />
        <Route path="partner/verify/:token" element={<PartnerVerify />} />
        {/* Partner admin — gated by X-Admin-Secret, public URL */}
        <Route path="partner/admin" element={<PartnerAdmin />} />
        {/* A genuinely unknown path — a stale link, a typo, an old bookmark
            past the /mahi-prefix redirects in App.js. The bare root has its
            own index route above and never reaches here. Still branches on
            whether a session exists, same reasoning as that route: an
            anonymous visitor typing garbage into the address bar is closer
            to a prospective signup than someone who should be bounced
            through Guarded into /login. */}
        <Route
          path="*"
          element={<Navigate to={localStorage.getItem('mira_token') ? '/discover' : '/signup'} replace />}
        />
      </Routes>
    </Suspense>
  </MahiAuthProvider>
);

export default MahiApp;
